import { FilterCard, CustomDataTable, useApi, Toaster, CustomAddCategoryModal, CustomBackdrop, validationMessages } from '../../utils/globalImports';
import { React, useState, useEffect, Box } from '../../utils/globalImports';
import { ApiResponse } from '../../services/apiService';
import { useConfirmCloseDialog } from '../../contexts/ConfirmCloseDialogContext';

interface Category {
    categoryId?: number;
    categoryName: string;
    status: boolean;
    description: string;
    createdId?: number;
    updatedId?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

const ClientCategory: React.FC = () => {

    // State to manage the modal visibility
    const [modalOpen, setModalOpen] = useState(false);
    const [categoryDatalist, setCategoryDatalist] = useState<Category[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<any | null>(null);
    const [categoryData, setCategoryData] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [currentStatus, setCurrentStatus] = useState<string[]>(['Active']);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [isOn, setIsOn] = useState<boolean>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

    const { execute, loading } = useApi();
    const { showConfirmCloseDialog } = useConfirmCloseDialog();
    const props = { showClientDropdown: false, title: false, actionText: 'Add Client Category', searchbarText: 'Search for a client category by name, description' };

    const userId = Number(localStorage.getItem("userId"));

    useEffect(() => {
        fetchCategories();
    }, []);  // The empty dependency array ensures it only runs once when the component mounts

    // Fetch categories
    const fetchCategories = async () => {
        const { data } = await execute<Category[]>('get', '/Lookup/GetCategories');
        if (data) {
            setCategoryDatalist(data);
    
            const categoryData = data.map((category) => {
                const { categoryId, createdAt, categoryName, description, status } = category; 
                const Status = status ? 'Active' : 'Inactive';
    
                return { id: categoryId, createdAt, categoryId, Name: categoryName, description, Status };
            });
    
            setCategoryData(categoryData);
            const activeCategories = categoryData
                .filter((category) => currentStatus.includes(category.Status))
                .sort((a, b) => a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' }));
                setFilteredData(activeCategories);
        }
    };

    // Filter Data
    const handleSearchGridData = (query: string, status: Number) => {
        const numericStatus = Number(status); // Convert status to a number
        const lowerCaseQuery = query.toLowerCase();
    
        // Map statuses to numeric values for filtering
        const statusMapping: Record<string, number> = {
            Active: 1,
            Inactive: 0,
        };
    
        // Ensure that categoryData is properly filtered
        const filtered = categoryData.filter((item) => {
            const itemStatus = statusMapping[item.Status] ?? -1; // Ensure mapping exists
            const matchesQuery =
                item.Name.toLowerCase().includes(lowerCaseQuery) || //Search in categoryName
                (item.description && item.description.toLowerCase().includes(lowerCaseQuery)); //Search in description
    
            const matchesStatus =
                numericStatus === -1 || // If -1 (show all)
                itemStatus === numericStatus; // Match the exact status
    
            return matchesQuery && matchesStatus;
        }).sort((a, b) =>
            a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' })
        );
    
        //Update filteredData with correctly filtered categories
        setFilteredData(filtered);
    
        //Ensure currentStatus is correctly updated
        setCurrentStatus(
            numericStatus === 0
                ? ['Inactive']
                : numericStatus === -1
                    ? ['Active', 'Inactive']
                    : numericStatus === 1
                        ? ['Active']
                        : currentStatus
        );
    };      

    const handleFormSubmit = async (formData: any, setBackendErrors?: (errors: any) => void) => {
        let response: ApiResponse<any> = { data: null, error: null };
        if (selectedCategory) {
            const updatedCategory = categoryDatalist.find((category) => category.categoryId === selectedCategory.categoryId);
            // If the category is found, update the fields
            if (updatedCategory) {
                // Step 2: Update the fields on the filtered record
                const updatedCategoryData = {
                    ...updatedCategory, // Keep the original category data
                    categoryName: formData.categoryName,
                    description: formData.description,
                    status: formData.status === 'Active' ? true : false, // Update based on Status
                    updatedId: userId,
                };

                response = await execute('put', '/Lookup/UpdateCategory', updatedCategoryData);
            }
        } else {

            // Adding a new category
            const category: Category = {
                categoryName: formData.categoryName,
                description: formData.description,
                status: formData.status === 'Active' ? true : false,
                createdId: userId,
            };

            response = await execute('post', '/Lookup/CreateCategory', category);
        }

        if (response && response.error) {
            if (
              typeof response.error === 'string' &&
              response.error.includes('Category name already exists')
            ) {
              setBackendErrors?.({ name: response.error });
              return false; // Don't close modal
            } else {
              setToast({ open: true, message: response.error, type: 'error' });
              return false; // Don't close modal on other errors
            }
          } else {
            // Submission successful – close the modal
            setToast({ open: true, message: response?.data, type: 'success' });
            setModalOpen(false);
            setSelectedCategory(null);
            handleContentChange(false);
            fetchCategories();
            return true;
          }
    };      

    const handleEdit = (category: any) => {
        setSelectedCategory({
            ...category,
            categoryName: category.Name,
            status: category.Status === 'Active' ? 'Active' : 'Inactive',
            }); 
        handleOpenModal(); // Open the modal
    };

    const handleAdd = () => {
        setSelectedCategory(null);
        setModalOpen(true);
    };

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleToggleState = (row: any) => {
        if (row.Status === 'Inactive') {
            setIsOn(false)
        } else {
            setIsOn(true)
        }
        setSelectedCategory(row);
        setDialogOpen(true);
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
        setSelectedCategory(null); 
    };

    const handleDialogConfirm = async () => {
        if (!selectedCategory) return;
        try {
            // Find the selected category in the list
            const updatedCategory = categoryDatalist.find(
                (category) => category.categoryId === selectedCategory.categoryId
            );
    
            if (updatedCategory) {
                //Toggle the status dynamically
                const newStatus = updatedCategory.status ? false : true;
    
                const updatedCategoryData = {
                    ...updatedCategory,
                    status: newStatus, //Toggle between true (Active) and false (Inactive)
                    updatedId: userId,
                };
    
                //Make the API call to update the status
                const response = await execute('put', '/Lookup/UpdateCategory', updatedCategoryData);
                if (response?.error) {
                    setToast({ open: true, message: response.error, type: 'error' });
                  } else {
                    setToast({ open: true, message: 'Status updated successfully', type: 'success' });
                  }
            }
            //Refresh the category list
            fetchCategories();
            setSelectedCategory(null);
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setDialogOpen(false);
        }
    };    

    const handleContentChange = (state:boolean) => {
    setHasUnsavedChanges(state); 
    };

  const handleClose=()=>{
    if (hasUnsavedChanges) { 
        showConfirmCloseDialog({
          onConfirm: () => { 
            setHasUnsavedChanges(false);  
            setModalOpen(false);       
          },
          onCancel: () => { 
          },
        });
      }
      else{
        setModalOpen(false);
      } 
  }

    return (
        <>
            <FilterCard {...props} handleModalOrNavigate={handleAdd} handleSearch={handleSearchGridData} />
            <CustomAddCategoryModal handleContentChange={handleContentChange} open={modalOpen} onClose={handleClose} onSubmit={handleFormSubmit} categoryData={selectedCategory} />
            {categoryDatalist.length > 0 && (
                <CustomDataTable
                    rows={filteredData}
                    actionText="Client Category"
                    sortOptions={[{ value: "Name", text: "Name" }]}
                    onRowEdit={handleEdit}
                    onToggleClick={handleToggleState}
                    isDialogOpen={dialogOpen}
                    isToggleOn={isOn}
                    handleCancel={handleDialogCancel}
                    handleConfirm={handleDialogConfirm}
                    // gridHeight={'calc(100vh - 500px)'}
                    noResultMessage={validationMessages.common_RecordNotFound("client categories")}
                />)}
            <CustomBackdrop open={loading} />
            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
        </>
    );
};

export default ClientCategory;